import React, { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useAdminAuth } from "./AdminAuthContext";
import BgLogo from "../components/BgLogo";
import { Loader2, ShieldCheck, AlertTriangle, Lock } from "lucide-react";

function formatErr(detail) {
  if (!detail) return "Login failed. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg || JSON.stringify(d)).join(" ");
  return String(detail);
}

export default function AdminLoginPage() {
  const { admin, login, loading } = useAdminAuth();
  const nav = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/admin/messages";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (admin) return <Navigate to={from} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav(from, { replace: true });
    } catch (e2) {
      setErr(formatErr(e2?.response?.data?.detail) || e2.message);
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    "w-full border border-white/15 bg-[#0A0A0A] px-4 py-3 text-white placeholder-white/30 transition-colors focus:border-brand-red focus:outline-none focus:ring-2 focus:ring-brand-red/40";

  return (
    <main
      className="relative flex min-h-screen items-center justify-center bg-brand-bg px-6 py-12"
      data-testid="page-admin-login"
    >
      <div className="absolute inset-0 grid-bg opacity-30" />
      <div className="absolute inset-0 radial-glow opacity-60" />
      <div className="relative w-full max-w-md">
        <div className="mb-8 flex justify-center">
          <BgLogo />
        </div>
        <div className="border border-white/10 bg-[#0A0A0A] p-8 sm:p-10">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-brand-textDim">
            <ShieldCheck className="h-3.5 w-3.5 text-brand-red" />
            Restricted area
          </div>
          <h1 className="mt-3 font-heading text-3xl tracking-tight text-white">Admin sign in</h1>
          <p className="mt-2 text-sm text-brand-textDim">
            Internal access only. All sign-in attempts are logged.
          </p>

          <form className="mt-8 space-y-5" onSubmit={submit} data-testid="admin-login-form">
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-brand-textDim">
                Email <span className="text-brand-red">*</span>
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={`mt-2 ${inputClass}`}
                data-testid="admin-login-email"
                autoComplete="username"
              />
            </label>
            <label className="block">
              <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-brand-textDim">
                Password <span className="text-brand-red">*</span>
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={`mt-2 ${inputClass}`}
                data-testid="admin-login-password"
                autoComplete="current-password"
              />
            </label>

            {err && (
              <div
                className="flex items-center gap-2 border border-brand-red/40 bg-brand-red/10 px-3 py-2 text-sm text-brand-red"
                data-testid="admin-login-error"
              >
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {err}
              </div>
            )}

            <button
              type="submit"
              disabled={busy || loading}
              data-testid="admin-login-submit"
              className="inline-flex w-full items-center justify-center gap-2 bg-brand-red px-6 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors hover:bg-brand-redHover disabled:cursor-not-allowed disabled:opacity-70"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
              {busy ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </div>
        <a
          href="/"
          className="mt-6 block text-center font-mono text-[10px] uppercase tracking-[0.25em] text-brand-textDim hover:text-white"
          data-testid="admin-back-link"
        >
          ← Back to website
        </a>
      </div>
    </main>
  );
}
